import { differenceInCalendarDays, isBefore } from 'date-fns';

import {
  getPriceWithCurrency,
  IBilling,
  IPaymentInfo,
  PaymentInfoServer,
  PaymentState
} from 'shared/entities/payment';
import { normalizeBilling } from 'shared/entities/payment/normalizers';
import { IRootStore } from 'shared/entities/store/rootStore';
import { MINIMAL_PAYMENT } from 'stores/paymentStore/config';
import { TariffKind } from 'shared/entities/tariff';

import ListModel from '../ListModel';

import PaymentMethodModel from './PaymentMethodModel';

export const CORPORATE_TARIFF_PRICE = 60000;

export default class PaymentInfoModel implements IPaymentInfo {
  private _minimalPayment: number;

  readonly methods: ListModel<PaymentMethodModel>;
  readonly billing: IBilling;
  readonly daysLeft: number;
  readonly dayPrice: number;
  readonly monthPrice: number;
  readonly shouldPay: boolean;
  readonly usersCount: number;
  readonly nextPaymentDate: Date;
  readonly bankruptDate: Date;
  readonly isProfitable: boolean;
  readonly maxUsersCount: number | null;
  readonly optimalTariff: TariffKind;
  readonly paymentsCount: number | null;
  readonly invoiceAutogeneratedDate: Date | null;
  readonly rootStore: IRootStore;

  constructor({
    dayPrice,
    monthPrice,
    methods,
    daysLeft,
    billing,
    shouldPay,
    usersCount,
    nextPaymentDate,
    bankruptDate,
    isProfitable,
    maxUsersCount,
    minimalPayment,
    optimalTariff,
    paymentsCount,
    invoiceAutogeneratedDate,
    rootStore
  }: {
    methods: ListModel<PaymentMethodModel>;
    billing: IBilling;
    daysLeft: number;
    dayPrice: number;
    monthPrice: number;
    usersCount: number;
    maxUsersCount: number | null;
    shouldPay: boolean;
    nextPaymentDate: Date;
    bankruptDate: Date;
    isProfitable: boolean;
    minimalPayment: number;
    optimalTariff: TariffKind;
    paymentsCount: number | null;
    invoiceAutogeneratedDate: Date | null;
    rootStore: IRootStore;
  }) {
    this.methods = methods;
    this.billing = billing;
    this.daysLeft = daysLeft;
    this.dayPrice = dayPrice;
    this.monthPrice = monthPrice;
    this.shouldPay = shouldPay;
    this.usersCount = usersCount;
    this.nextPaymentDate = nextPaymentDate;
    this.bankruptDate = bankruptDate;
    this.isProfitable = isProfitable;
    this._minimalPayment = minimalPayment;
    this.maxUsersCount = maxUsersCount;
    this.optimalTariff = optimalTariff;
    this.paymentsCount = paymentsCount;
    this.invoiceAutogeneratedDate = invoiceAutogeneratedDate;
    this.rootStore = rootStore;
  }
  // TO DO Убрать,  когда бэк пофиксит, что для vip тарифа приходит 0
  get minimalPayment(): number {
    return this._minimalPayment || MINIMAL_PAYMENT;
  }

  get roundedBillingBalance(): number {
    return Math.round(this.billing.balance);
  }

  get balanceString(): string {
    return getPriceWithCurrency(this.rootStore, {
      value: this.billing.balance,
      fractionDigits: 2
    });
  }

  get roundedBalanceString(): string {
    return getPriceWithCurrency(this.rootStore, {
      value: this.billing.balance
    });
  }

  get dayPriceString(): string {
    return getPriceWithCurrency(this.rootStore, {
      value: Math.round(this.dayPrice)
    });
  }

  get monthPriceString(): string {
    return getPriceWithCurrency(this.rootStore, {
      value: Math.round(this.monthPrice)
    });
  }

  get corporateMonthPriceString(): string {
    return getPriceWithCurrency(this.rootStore, {
      value: CORPORATE_TARIFF_PRICE
    });
  }

  get minimalPaymentString(): string {
    return getPriceWithCurrency(this.rootStore, {
      value: this.minimalPayment
    });
  }

  get state(): PaymentState {
    if (this.billing.balance <= 0) {
      if (this.methods.length > 0) {
        return PaymentState.receivingMoneyNotAvailable;
      }
      return PaymentState.subscriptionAvailable;
    }

    if (this.methods.length > 0) {
      return PaymentState.subscriptionActive;
    }
    return PaymentState.subscriptionStopped;
  }

  get isSubscriptionActive(): boolean {
    return this.billing.balance > 0 && this.methods.length > 0;
  }

  get isTrialOver(): boolean {
    return isBefore(this.billing.trialUntil, new Date());
  }

  get trialDaysLeft(): number {
    const diff = differenceInCalendarDays(this.billing.trialUntil, new Date());

    return diff > 0 ? diff : 0;
  }

  remove = async (id: string): Promise<BaseResponse> => {
    const method = this.methods.getEntity(id);

    if (!method) {
      return {
        isError: true
      };
    }

    const response = await method.remove();

    if (response.isError) {
      return {
        isError: true
      };
    }

    this.methods.removeEntity(id);

    return {
      isError: false
    };
  };

  static fromJson(
    raw: PaymentInfoServer,
    rootStore: IRootStore
  ): PaymentInfoModel {
    const { entities, keys } = raw.methods.reduce(
      (acc, method) => ({
        ...acc,
        entities: {
          ...acc.entities,
          [method._id]: PaymentMethodModel.fromJson(method, rootStore)
        },
        keys: [...acc.keys, method._id]
      }),
      { entities: {}, keys: [] }
    );

    return new PaymentInfoModel({
      methods: new ListModel<PaymentMethodModel>({ entities, keys }),
      billing: normalizeBilling(raw.billing),
      daysLeft: raw.days_left ?? 0,
      dayPrice: raw.day_price,
      maxUsersCount: raw.max_users_count,
      monthPrice: raw.month_price,
      shouldPay: raw.should_pay,
      usersCount: raw.users_count,
      nextPaymentDate: raw.next_payment_date
        ? new Date(raw.next_payment_date)
        : new Date(), // для корп тарифа
      bankruptDate: raw.bankrupt_date
        ? new Date(raw.bankrupt_date)
        : new Date(), // для корп тарифа
      isProfitable: raw.is_profitable,
      minimalPayment: raw.minimal_payment,
      optimalTariff: raw.optimal_tariff,
      paymentsCount: raw.payments_count ?? null,
      invoiceAutogeneratedDate: raw.date_invoice_autogenerated
        ? new Date(raw.date_invoice_autogenerated)
        : null,
      rootStore
    });
  }
}
